import { useState, useEffect, useContext } from 'react';
import { AuthContext } from "../../../auth/Auth";
import { BoardSchemeInterface } from 'ts-interfaces';
import { useBoards } from '../../../global-state/useBoards';
import { useProjectData } from '../../../MattoState';
import { generateID } from '../../../libs/util';
import { uploadAndSave } from '../../../libs/firebase';
import { associateMultipleBoardsWithProject } from './../../../v2/apis/boardsSchemeApi';

export const useBoardManagement = (
  projectId: string,
  boards: BoardSchemeInterface[],
  setBoards: (projectId: string, boards: BoardSchemeInterface[], userId: string, permissions: any) => Promise<void>,
  permissions: any,
  setSnackbarMessage: (message: string) => void
) => {
  const { currentUser } = useContext(AuthContext);
  const [openBoardModal, setOpenBoardModal] = useState(false);
  const [selectedBoardIds, setSelectedBoardIds] = useState<Set<string>>(new Set());
  const [openBoardChoiceModal, setOpenBoardChoiceModal] = useState(false);
  const [initialSelectedBoardIds, setInitialSelectedBoardIds] = useState<Set<string>>(new Set());
  const projectDefaultData = useProjectData();

  // Initialize selected board IDs when boards change
  useEffect(() => {
    if (boards && boards.length > 0) {
      const currentBoardIds = new Set(boards.map(board => board.id));
      setSelectedBoardIds(currentBoardIds);
      setInitialSelectedBoardIds(currentBoardIds);
    }
  }, [boards]);

  const handleOpenBoardModal = () => {
    setOpenBoardChoiceModal(true);
  };

  const handleCloseBoardModal = () => {
    setOpenBoardModal(false);
    // Reset selections to current state when closing without saving
    setSelectedBoardIds(new Set(boards.map(board => board.id)));
  };

  const handleCloseBoardChoiceModal = () => {
    setOpenBoardChoiceModal(false);
  };

  const handleAddExistingBoard = () => {
    setOpenBoardChoiceModal(false);
    setOpenBoardModal(true);
  };

  const handleCreateNewBoard = async () => {
    setOpenBoardChoiceModal(false);
    setSnackbarMessage('Creating new board...');
    
    try {
      const boardId = generateID();
      const newBoard: BoardSchemeInterface = {
        id: boardId,
        name: projectDefaultData.title || 'New Board',
        user: currentUser.uid,
        owner: currentUser.uid,
        archive: false,
        backgroundTexture: projectDefaultData.backgroundTexture || null,
        lighting: projectDefaultData.lighting || null,
        cameraPosition: projectDefaultData.cameraPosition || null,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        projectData: {
          title: projectDefaultData.title,
          userId: currentUser.uid,
          envmap: projectDefaultData.envmap,
          projectId: boardId,
          scene: projectDefaultData.scene,
          cameraMatrix: null,
          associatedProjectId: projectId,
        },
        physicalObjects: [],
        controlsTarget: projectDefaultData.controlsTarget || [0, 0, 0],
        associatedProjectId: projectId,
      };
      
      await handleBoardUpdate(newBoard, true);
      await uploadAndSave(newBoard.id, 'projects', currentUser.uid, newBoard);
      setSnackbarMessage('New board created successfully');
    } catch (error) {
      setSnackbarMessage('Failed to create new board');
    }
  };

  const handleBoardSelectionChange = (boardId: string, isChecked: boolean) => {
    const newSelectedIds = new Set(selectedBoardIds);
    if (isChecked) {
      newSelectedIds.add(boardId);
    } else {
      newSelectedIds.delete(boardId);
    }
    setSelectedBoardIds(newSelectedIds);
  };

  const handleSaveBoardAssociations = async () => {
    setSnackbarMessage('Updating board associations...');
    try {
      const boardsState = useBoards.getState();
      const allAvailableBoards = [
        ...boardsState.userBoards,
        ...boardsState.sharedBoards
      ];
      
      // Get boards that were initially selected (project's current boards)
      const initiallySelected = Array.from(initialSelectedBoardIds);
      
      // Get boards that are currently selected
      const currentlySelected = Array.from(selectedBoardIds);
      
      // Find boards to add (newly selected)
      const boardsToAdd = currentlySelected.filter(id => !initiallySelected.includes(id));
      
      // Find boards to remove (were selected, now unselected)
      const boardsToRemove = initiallySelected.filter(id => !currentlySelected.includes(id));
      
      await associateMultipleBoardsWithProject(boardsToAdd, boardsToRemove, projectId, currentUser.uid, permissions);
      // Get the actual board objects for boards to add
      const newBoardsToAdd = allAvailableBoards.filter(board => 
        boardsToAdd.includes(board.id) && board.archive !== true
      );
      
      // Start with current project boards
      let updatedBoards = [...boards];
      
      // Remove boards that the user deselected
      updatedBoards = updatedBoards.filter(board => !boardsToRemove.includes(board.id));
      
      // Add newly selected boards
      updatedBoards = [...updatedBoards, ...newBoardsToAdd];
      
      // Remove duplicates (in case of any edge cases)
      const uniqueBoards = updatedBoards.filter((board, index, self) => 
        index === self.findIndex(b => b.id === board.id)
      );
      
      await setBoards(projectId, uniqueBoards, currentUser.uid, permissions);
      boardsState.syncBoardAssociations(boardsToAdd, boardsToRemove, projectId);
      
      setSnackbarMessage('Board associations updated successfully');
      handleCloseBoardModal();
      
      // Update the initial state for next time
      setInitialSelectedBoardIds(new Set(uniqueBoards.map(b => b.id)));
      setSelectedBoardIds(new Set(uniqueBoards.map(b => b.id)));
    } catch (error) {
      console.error('Error updating board associations:', error);
      setSnackbarMessage('Failed to update board associations');
    }
  };

  const handleBoardUpdate = async (board: BoardSchemeInterface, addBoard = false) => {
    if (addBoard) {
      setSnackbarMessage('Creating new board...');
      await setBoards(projectId, [board, ...boards], currentUser.uid, permissions);
      setSnackbarMessage('New board created successfully');
    } else {
      setSnackbarMessage('Saving board updates...');
      const updatedBoards = boards.map(b => b.id === board.id ? board : b);
      await setBoards(projectId, updatedBoards, currentUser.uid, permissions);
      setSnackbarMessage('Board updates saved successfully');
    }
  };

  return {
    // State
    openBoardModal,
    selectedBoardIds,
    openBoardChoiceModal,
    
    // Actions
    handleOpenBoardModal,
    handleCloseBoardModal,
    handleCloseBoardChoiceModal,
    handleAddExistingBoard,
    handleCreateNewBoard,
    handleBoardSelectionChange,
    handleSaveBoardAssociations,
    handleBoardUpdate
  };
};